import { useRef, type ClipboardEvent, type KeyboardEvent } from 'react'
import { cn } from '@/components/ui'
import { t } from '@/lib/i18n'
import { sanitizeCode } from '../utils'

export interface OtpInputProps {
  value: string
  onChange: (value: string) => void
  /** Called once all digits are filled (typing or paste). */
  onComplete?: (value: string) => void
  length?: number
  disabled?: boolean
  invalid?: boolean
  autoFocus?: boolean
  /** Accessible name of the group. */
  label?: string
  className?: string
}

/** Six digit code boxes: auto advance, backspace goes back, arrows move, paste fills every box. */
export function OtpInput({ value, onChange, onComplete, length = 6, disabled, invalid, autoFocus, label, className }: OtpInputProps) {
  const refs = useRef<Array<HTMLInputElement | null>>([])
  // Set while we move focus ourselves, so the contiguity check below only reacts to clicks.
  const programmatic = useRef(false)
  const digits = Array.from({ length }, (_, i) => value[i] ?? '')

  const focusAt = (i: number) => {
    const el = refs.current[Math.max(0, Math.min(length - 1, i))]
    programmatic.current = true
    el?.focus()
    el?.select()
    programmatic.current = false
  }

  const commit = (next: string) => {
    const clean = sanitizeCode(next, length)
    onChange(clean)
    if (clean.length === length) onComplete?.(clean)
  }

  const setDigitAt = (i: number, raw: string) => {
    let typed = sanitizeCode(raw, length)
    // Typing over a filled box without a selection gives "old + new": keep the new digit.
    if (typed.length === 2 && digits[i] && typed.includes(digits[i])) typed = typed.replace(digits[i], '')
    if (!typed) return
    if (typed.length > 1) {
      // Autofill or a fast paste into one box: spread from this position.
      const merged = (value.slice(0, i) + typed).slice(0, length)
      commit(merged)
      focusAt(merged.length)
      return
    }
    const next = (value.slice(0, i) + typed + value.slice(i + 1)).slice(0, length)
    commit(next)
    if (i < length - 1) focusAt(i + 1)
  }

  const onKeyDown = (i: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      e.preventDefault()
      if (value[i]) {
        onChange(value.slice(0, i) + value.slice(i + 1))
      } else if (i > 0) {
        onChange(value.slice(0, i - 1) + value.slice(i))
        focusAt(i - 1)
      }
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault()
      focusAt(i - 1)
    } else if (e.key === 'ArrowRight') {
      e.preventDefault()
      focusAt(i + 1)
    }
  }

  const onPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const text = sanitizeCode(e.clipboardData.getData('text'), length)
    if (!text) return
    e.preventDefault()
    commit(text)
    focusAt(text.length)
  }

  return (
    <div role="group" aria-label={label ?? t('Verification code')} className={cn('flex justify-between gap-2', className)}>
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el
          }}
          value={d}
          onChange={(e) => setDigitAt(i, e.target.value)}
          onKeyDown={(e) => onKeyDown(i, e)}
          onPaste={onPaste}
          onFocus={(e) => {
            // Keep the code contiguous: focusing past the first empty box jumps back to it.
            if (!programmatic.current && i > value.length) focusAt(value.length)
            else e.target.select()
          }}
          inputMode="numeric"
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          pattern="[0-9]*"
          maxLength={length}
          disabled={disabled}
          autoFocus={autoFocus && i === 0}
          aria-label={t('Digit {n} of {total}', { n: i + 1, total: length })}
          aria-invalid={invalid || undefined}
          data-otp-index={i}
          className={cn(
            'h-12 w-full min-w-0 max-w-12 rounded-control border border-line-strong bg-surface text-center text-[20px] font-semibold tabular-nums text-text',
            'transition-[border-color,box-shadow] duration-150 ease-out outline-none',
            'focus:border-focus focus:ring-3 focus:ring-focus-halo disabled:cursor-not-allowed disabled:opacity-50',
            invalid && 'border-danger focus:ring-danger/20',
          )}
        />
      ))}
    </div>
  )
}
